/**
 * ClientSlotsPicker — Sélection tactile des créneaux côté client.
 * ────────────────────────────────────────────────────────────────
 * S'affiche juste sous la NextActionCard quand `delivery.status === 'proposed'`.
 * Une seule interaction : tap sur une tuile → « Confirmer ce créneau ».
 *
 * Une fois confirmé (client OU agence), les autres propositions disparaissent
 * automatiquement (l'API réduit la liste au créneau retenu).
 */
import { useState } from "react";
import { Check, MapPin, Loader2, AlertCircle } from "lucide-react";
import { Card, MicroLabel, Button } from "@/components/wyvio-ui";
import api from "@/lib/api";

const ClientSlotsPicker = ({ delivery, onConfirmed }) => {
  const [pending, setPending] = useState(null);   // slot_id en cours
  const [selected, setSelected] = useState(null); // slot_id sélectionné visuellement
  const [error, setError] = useState("");

  const proposals = delivery?.proposals || [];

  const submit = async (slotId) => {
    if (pending) return;
    setSelected(slotId);
    setError("");
    setPending(slotId);
    try {
      await api.clientSelectSlot(slotId);
      await onConfirmed?.();
    } catch (e) {
      setError(e.message || "Erreur, réessayez.");
      setSelected(null);
    } finally {
      setPending(null);
    }
  };

  if (!proposals.length) return null;

  return (
    <div data-testid="client-slots-picker" className="space-y-3">
      <div className="flex items-center justify-between">
        <MicroLabel>Créneaux disponibles</MicroLabel>
        <span className="text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3]">
          {proposals.length} propositions
        </span>
      </div>

      <div className="space-y-2.5">
        {proposals.map((slot) => {
          const isSelected = selected === slot.id;
          const isPending = pending === slot.id;
          return (
            <button
              key={slot.id}
              onClick={() => submit(slot.id)}
              disabled={Boolean(pending)}
              data-testid={`client-slot-tile-${slot.id}`}
              className={[
                "w-full text-left group",
                "rounded-2xl border-2 p-5",
                "transition-all duration-200 ease-out",
                "flex items-center gap-4",
                isSelected
                  ? "border-[#C8FF2F] bg-[#F3FCE1] dark:bg-[#1F2A0A]"
                  : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A] active:scale-[0.995]",
                pending && !isPending ? "opacity-50" : "",
              ].join(" ")}
            >
              <div
                className={[
                  "h-11 w-11 rounded-2xl flex items-center justify-center shrink-0",
                  "transition-colors duration-200",
                  isSelected
                    ? "bg-[#C8FF2F] text-[#0A0A0A]"
                    : "bg-[#F3F4F6] dark:bg-[#1A1A1A] text-[#0A0A0A] dark:text-white",
                ].join(" ")}
              >
                {isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Check
                    size={18}
                    strokeWidth={2.5}
                    className={isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-40"}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-[16px] sm:text-[17px] font-semibold tracking-[-0.01em] text-[#0A0A0A] dark:text-white leading-tight"
                  data-testid={`client-slot-label-${slot.id}`}
                >
                  {slot.label}
                </p>
                <p className="mt-1 text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3]">
                  Tap pour confirmer ce créneau
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {delivery?.location && (
        <Card className="p-4 flex items-center gap-3 mt-3">
          <div className="h-9 w-9 rounded-xl bg-[#F3F4F6] dark:bg-[#1A1A1A] text-[#6B7280] flex items-center justify-center shrink-0">
            <MapPin size={14} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-[#6B7280] dark:text-[#A3A3A3]">
              Lieu du rendez-vous
            </p>
            <p className="text-[13px] font-semibold text-[#0A0A0A] dark:text-white truncate">
              {delivery.location}
            </p>
          </div>
        </Card>
      )}

      {error && (
        <div
          data-testid="client-slot-error"
          className="rounded-xl bg-[#FEE2E2] dark:bg-[#3B0F0F] text-[#991B1B] dark:text-[#FCA5A5] text-[12.5px] px-3 py-2 flex items-center gap-2"
        >
          <AlertCircle size={14} />
          {error}
        </div>
      )}
    </div>
  );
};

export default ClientSlotsPicker;
